import { z } from 'zod';

// ─── treatment plan signature merge vocabularies ───────────────────────────
//
// One `treatment_plan_signature_merges` row per Healthie signature submission:
// the claim that lets Galapagos ask Healthie to merge the member's signature
// form with their treatment plan note at most once. The `as const` array
// drives both the CHECK rendered in the DB schema and the api's types
// (ADR-056 Rule 8); readers consume `typeof treatmentPlanSignatureMerges.$inferSelect`
// (ADR-066).

/**
 * Where a signature submission stands.
 *
 * `pending`: claimed, merge call in flight (or its worker died mid-call).
 * `requested`: Healthie accepted `generateChartingPdf`. `unresolved`: the call
 * failed in a way that leaves Healthie's action unknown, so it is never retried
 * and care ops follow up unless a matching document later confirms it.
 * `confirmed`: Healthie's `document.created` for the combined PDF was seen.
 * `no_treatment_plan` / `no_healthie_user` / `healthie_rejected`: terminal,
 * nothing was merged.
 */
export const TREATMENT_PLAN_SIGNATURE_MERGE_OUTCOMES = [
  'pending',
  'requested',
  'unresolved',
  'confirmed',
  'no_treatment_plan',
  'no_healthie_user',
  'healthie_rejected',
] as const;
export const treatmentPlanSignatureMergeOutcomeSchema = z.enum(
  TREATMENT_PLAN_SIGNATURE_MERGE_OUTCOMES,
);
export type TreatmentPlanSignatureMergeOutcome = z.infer<
  typeof treatmentPlanSignatureMergeOutcomeSchema
>;

/** Upper bound on the Healthie id columns, which are short numeric strings. */
export const TREATMENT_PLAN_SIGNATURE_ID_MAX_LENGTH = 255;
